import { mat4, quat, vec2, vec3 } from 'gl-matrix'
import generateUUID from '../../../_utils/math/generateUUID'

export default class Object3D {
  constructor() {
    this.children = [];

    this.matrix = mat4.create();
    this.modelMatrix = mat4.create();

    this._modelMatrixInverse = mat4.create();

    this.quaternion = quat.create();
    this.position = vec3.create();
    this.scale = vec3.create();
    vec3.set(this.scale, 1, 1, 1);

    this.parent = null;
    this.isVisible = true;
    this.renderOrder = 0;
    this.isRenderable = false;
    this._uuid = generateUUID();

    this._isDisposed = false;
  }

  get uuid() {
    return this._uuid;
  }

  get isDesposed() {
    return this._isDisposed;
  }

  add(object) {
    this.children.push(object);
    object.parent = this;
  }

  remove(object) {
    var index = this.children.indexOf(object);
    if ( index !== - 1 ) {
      object.parent = null;
      this.children.splice( index, 1 );
    }
  }

  setRotationFromEuler(x, y, z) {
    quat.fromEuler(this.quaternion, x, y, z);
  }

  rotateX(angle) { quat.rotateX(this.quaternion, this.quaternion, angle); }
  rotateY(angle) { quat.rotateY(this.quaternion, this.quaternion, angle); }
  rotateZ(angle) { quat.rotateZ(this.quaternion, this.quaternion, angle); }

  rotateOnAxis(axis, angle) {
    quat.setAxisAngle(this.quaternion, axis, angle);
  }

  applyMatrix(matrix) {
    mat4.multiply(this.matrix, matrix, this.matrix);
    mat4.getTranslation(this.position, this.matrix);
    mat4.getRotation(this.quaternion, this.matrix);
    mat4.getScaling(this.scale, this.matrix);
  }

  updateMatrix() {
    const x = this.quaternion[0], y = this.quaternion[1], z = this.quaternion[2], w = this.quaternion[3];
    const x2 = x + x,  y2 = y + y, z2 = z + z;
    const xx = x * x2, xy = x * y2, xz = x * z2;
    const yy = y * y2, yz = y * z2, zz = z * z2;
    const wx = w * x2, wy = w * y2, wz = w * z2;

    const sx = this.scale[0], sy = this.scale[1], sz = this.scale[2];

    mat4.set(
      this.matrix,

      ( 1 - ( yy + zz ) ) * sx,
      ( xy + wz ) * sx,
      ( xz - wy ) * sx,
      0,

      ( xy - wz ) * sy,
      ( 1 - ( xx + zz ) ) * sy,
      ( yz + wx ) * sy,
      0,

      ( xz + wy ) * sz,
      ( yz - wx ) * sz,
      ( 1 - ( xx + yy ) ) * sz,
      0,

      this.position[0],
      this.position[1],
      this.position[2],
      1
    );

    if(!this.parent) {
      mat4.copy(this.modelMatrix, this.matrix);
    } else {
      mat4.multiply(this.modelMatrix, this.parent.modelMatrix, this.matrix);
    }

    mat4.invert(this._modelMatrixInverse, this.modelMatrix);

    for (let i = 0, l = this.children.length; i < l; i++) {
      this.children[i].updateMatrix();
    }
  }

  updateTextureUnitIndicies(textureUnitIndexCnt = 0) {
    return this._setTextureUnitIndicies(textureUnitIndexCnt);
  }

  setGLContext(gl) {
    for (let i = 0, l  = this.children.length; i < l; i++) {
      this.children[i].setGLContext(gl);
    }
  }

  dispose(disposeAll = false) {
    if(this._isDisposed) return;

    let child;
    for (let i = 0, l  = this.children.length; i < l; i++) {
      child = this.children[i];
      if(child) child.dispose(disposeAll);
    }
    if(this.parent) this.parent.remove(this);

    this.isRenderable = false;
    this.isVisible = false;
    this.children = null;
    this.matrix = null;
    this.modelMatrix = null;
    this._modelMatrixInverse = null;
    this.quaternion = null;
    this.position = null;
    this.scale = null;

    this._isDisposed = true;
  }

  _setTextureUnitIndicies(textureUnitIndexCnt) {
    if(this.isRenderable) {
      textureUnitIndexCnt = this.material.setTextureUnitIndicies(textureUnitIndexCnt);
    }
    for (let i = 0, l = this.children.length; i < l; i++) {
      textureUnitIndexCnt = this.children[i]._setTextureUnitIndicies(textureUnitIndexCnt);
    }
    return textureUnitIndexCnt;
  }
}
